// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from "react";
import "./ClientSettings.scss";

import { useTranslation } from "react-i18next";
import { firestore } from '../../context/FirebaseConfig';
import { query, collection, doc, setDoc, getDocs, onSnapshot } from 'firebase/firestore';
import Switch from "react-switch";

import ConfirmationMessage from "../../components/ConfirmationMessage/ConfirmationMessage";
import StringList from "./StringList/StringList";
import TimezoneDropdown from "../../components/TimezoneDropdown/TimezoneDropdown";

export default function UsersTable({ client }) {
    const { i18n, t } = useTranslation();
    const [displayName, setDisplayName] = React.useState(client.company_displayname);
    const [clientData, setClientData] = React.useState({});
    const [lowAccuracyFlags, setLowAccuracyFlags] = React.useState([]);
    const [clientLabelsets, setClientLabelsets] = React.useState([]);
    const [clientSettingsDelivery, setClientSettingsDelivery] = React.useState({});
    const [clientSettingsClaim, setClientSettingsClaim] = React.useState({});
    const [clientSettingsDashboard, setClientSettingsDashboard] = React.useState({});
    const [showConfirmSaveMessage, setShowConfirmSaveMessage] = React.useState(false);
    const unsubscribeClientData = React.useRef(null);
    const unsubscribeClientSettingsDelivery = React.useRef(null);
    const unsubscribeClientSettingsClaim = React.useRef(null);
    const unsubscribeClientSettingsDashboard = React.useRef(null);

    React.useEffect(() => {
        setDisplayName(client.company_displayname);
        loadLowAccuracyFlags();
        subscribeClientData();
        subscribeClientSettingsDelivery();
        subscribeClientSettingsClaim();
        subscribeClientSettingsDashboard();
    }, [client])

    async function subscribeClientData() {
        const q = query(doc(firestore, "clients", client.company));
        const unsub = onSnapshot(q, (querySnapshot) => {
            const data = querySnapshot.data();
            if (!data?.timezone) {
                data.timezone = "Europe/Zurich";
            }
            setClientData(data);
        });
        unsubscribeClientData.current = unsub;
    }

    async function loadClientLabelsets() {
        const labelsetsNew = {};
        const q = query(collection(firestore, "clients", client.company, "labelsets"));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
            labelsetsNew[doc.id] = doc.data();
        });
        setClientLabelsets(labelsetsNew);
    }

    async function loadLowAccuracyFlags() {
        await loadClientLabelsets();
        if ("flag_low_accuracy" in client) {
            setLowAccuracyFlags(client.flag_low_accuracy);
        } else {
            setLowAccuracyFlags([]);
        }
    }

    async function subscribeClientSettingsDelivery() {
        if (unsubscribeClientSettingsDelivery.current) unsubscribeClientSettingsDelivery.current();

        const q = query(doc(firestore, "clients", client.company, "client_settings", "deliveries"));
        const unsub = onSnapshot(q, (querySnapshot) => {
            setClientSettingsDelivery(querySnapshot.data());
        });

        unsubscribeClientSettingsDelivery.current = unsub;
    }

    async function subscribeClientSettingsClaim() {
        if (unsubscribeClientSettingsClaim.current) unsubscribeClientSettingsClaim.current();

        const q = query(doc(firestore, "clients", client.company, "client_settings", "claim_settings"));
        const unsub = onSnapshot(q, (querySnapshot) => {
            setClientSettingsClaim(querySnapshot.data());
        });

        unsubscribeClientSettingsClaim.current = unsub
    }

    async function subscribeClientSettingsDashboard() {
        if (unsubscribeClientSettingsDashboard.current) unsubscribeClientSettingsDashboard.current();

        const q = query(doc(firestore, "clients", client.company, "client_settings", "dashboard_settings"));
        const unsub = onSnapshot(q, (querySnapshot) => {
            setClientSettingsDashboard(querySnapshot.data());
        });

        unsubscribeClientSettingsDashboard.current = unsub;
    }

    function handleDeliveryFeatureChange(feature, value) {
        setClientSettingsDelivery({ ...clientSettingsDelivery, features: {
            ...clientSettingsDelivery?.features,
            [feature]: value,
        } });
    }

    function handleDashboardGraphSettingsChange(graph, value) {
        setClientSettingsDashboard({ ...clientSettingsDashboard, activatedGraphs: {
            ...clientSettingsDashboard?.activatedGraphs,
            [graph]: value,
        } });
    }

    function handleTimezoneChange(e) {
        setClientData({ ...clientData, timezone: e.target.value });
    }

    async function onSave() {
        if (!displayName) {
            return;
        }

        // Save client settings
        await setDoc(doc(firestore, "clients", client.company), {
            company_displayname: displayName,
            flag_low_accuracy: lowAccuracyFlags.filter((flag) => { return flag != '' }),
            timezone: clientData.timezone
        }, { merge: true });

        // Save client delivery settings
        await setDoc(doc(firestore, "clients", client.company, "client_settings", "deliveries"), {
            features: clientSettingsDelivery.features,
        }, { merge: true });

        // Save client claim settings
        const filteredClaimEmails = (clientSettingsClaim?.claim_notification_emails ?? []).filter((email) => { return email != '' });
        await setDoc(doc(firestore, "clients", client.company, "client_settings", "claim_settings"), {
            claim_notification_emails: filteredClaimEmails,
        }, { merge: true });

        // Save client dashboard settings
        await setDoc(doc(firestore, "clients", client.company, "client_settings", "dashboard_settings"), {
            activatedGraphs: clientSettingsDashboard.activatedGraphs,
        }, { merge: true });

        setShowConfirmSaveMessage(true);
    }

    return <div className="client-settings">
        <div className="client-settings-header">
            <h3>{client.company_displayname}</h3>
            <button className="client-save-button" onClick={onSave}>{t("settings_save")}</button>
        </div>
        <label>
            {t("settings_client_name")}:
            <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>

        <StringList
            titleText={t("settings_client_low_accuracy_flag")}
            addButtonText={t("settings_client_add_low_accuracy_flag")}
            listElements={lowAccuracyFlags}
            setListElements={setLowAccuracyFlags}
            selectItems={Object.keys(clientLabelsets).reduce((acc, labelset) => {
                acc[labelset] = clientLabelsets[labelset].name;
                return acc;
            }, {})
            }
        />

        <StringList
            titleText="Claim Emails"
            addButtonText={t("settings_device_add_email")}
            listElements={clientSettingsClaim?.claim_notification_emails ?? []}
            setListElements={(emails) => setClientSettingsClaim({ ...clientSettingsClaim, claim_notification_emails: emails })}
        />

        <label className="timezone-settings-label">
            Timezone Setting:
            <TimezoneDropdown
                value={clientData.timezone}
                onChange={handleTimezoneChange}
                className="client-timezone-dropdown"
            />
        </label>

        <div className="settings_assistant_app_features">
            <p className="assistant_app_features_title">Delivery App Features</p>
            {["claims", "bale_feed_view", "mobile_labeling_requests"].map((feature) => {
                return <>
                    <Switch
                        checked={clientSettingsDelivery?.features?.[feature] ?? false}
                        onChange={() => {handleDeliveryFeatureChange(feature, !clientSettingsDelivery?.features?.[feature])}}
                        uncheckedIcon={false}
                        checkedIcon={false}
                    />
                    <label>{feature.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</label>
                </>
            })}
        </div>

        <div className="settings_assistant_app_features">
            <p className="assistant_app_features_title">Dashboard Settings - Activated Graphs</p>
            {[
                { key: "bale-count", nameKey: "timeline_bale_count_name" },
                { key: "material-distribution", nameKey: "timeline_material_distribution_name" },
                { key: "plastics-type-distribution", nameKey: "timeline_plastics_type_distribution_name" },
                { key: "contaminants", nameKey: "timeline_contaminants_name" },
                { key: "brown-paper", nameKey: "timeline_brown_paper_name" },
                { key: "volume", nameKey: "timeline_volume_flow_name" }
            ].map((graph) => {
                return <>
                    <Switch
                        checked={clientSettingsDashboard?.activatedGraphs?.[graph.key] ?? true}
                        onChange={() => {handleDashboardGraphSettingsChange(graph.key, !(clientSettingsDashboard?.activatedGraphs?.[graph.key] ?? true))}}
                        uncheckedIcon={false}
                        checkedIcon={false}
                    />
                    <label>{t(graph.nameKey)}</label>
                </>
            })}
        </div>

        <ConfirmationMessage message={t("settings_device_confirm_save")} show={showConfirmSaveMessage} setShow={setShowConfirmSaveMessage} />
    </div>
}